"use client";

import Link from "next/link";
import Image from "next/image";
import CampaignOverviewMap from "@/components/maps/CampaignOverviewMap";
import { clientLogger } from "@/lib/client-logger";
import { DEBUG_FLAGS } from "@/lib/debug-config";
import { isWhitelabelMode } from "@/lib/whitelabel-config";

interface Campaign {
  id: string;
  title: string;
  slug: string;
  description: string;
  orgName: string;
  orgDescription?: string;
  orgWebsite?: string;
  locationText?: string;
  coverImageUrl?: string;
  status: string;
  startAt: string | null;
  endAt: string | null;
  createdAt: string;
  participantCap?: number;
  mintCap?: number;
  isPublic: boolean;
  showMap?: boolean;
  participantCount: number;
}

interface Tenant {
  name: string;
  slug: string;
  logoUrl?: string;
  thumbnailLogoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
}

interface Clue {
  id: string;
  title: string;
  description: string;
  address: string;
  latitude?: number;
  longitude?: number;
  radiusMiles?: number;
  hideAddress?: boolean;
  slug: string;
  orderIndex: number;
}

interface CampaignDetailClientProps {
  campaign: Campaign;
  tenant: Tenant;
  clues: Clue[];
}

export function CampaignDetailClient({ campaign, tenant, clues }: CampaignDetailClientProps) {
  clientLogger.debug("CampaignDetailClient rendering", { campaign, tenant, clues });

  // Add a simple test div at the very top
  if (typeof window !== 'undefined') {
    clientLogger.debug("Client-side rendering confirmed");
  }
  const getStatusColor = (status: string) => {
    switch (status) {
      case "PUBLISHED":
        return "bg-blue-100 text-blue-800";
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "ENDED":
        return "bg-red-100 text-red-800";
      case "ARCHIVED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "PUBLISHED":
        return "Published";
      case "ACTIVE":
        return "Active";
      case "ENDED":
        return "Ended";
      case "ARCHIVED":
        return "Archived";
      default:
        return status;
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isActive = () => {
    const now = new Date();
    const start = campaign.startAt ? new Date(campaign.startAt) : null;
    const end = campaign.endAt ? new Date(campaign.endAt) : null;
    
    return campaign.status === "ACTIVE" || 
           (campaign.status === "PUBLISHED" && 
            (!start || start <= now) && 
            (!end || end >= now));
  };

  const canParticipate = isActive();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Debug: Component test banner */}
      {DEBUG_FLAGS.showMapDebugInfo && (
        <div className="bg-yellow-300 text-black p-4 text-center font-bold">
          🧪 COMPONENT TEST: CampaignDetailClient is rendering! Campaign: {campaign.title}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            {!isWhitelabelMode() && (
              <>
                <li>
                  <Link href="/directory" className="hover:text-gray-700">
                    Directory
                  </Link>
                </li>
                <li>→</li>
              </>
            )}
            <li>
              <Link href={`/${tenant.slug}`} className="hover:text-gray-700">
                {tenant.name}
              </Link>
            </li>
            <li>→</li>
            <li className="text-gray-900">{campaign.title}</li>
          </ol>
        </nav>

        {/* Cover Image or Placeholder */}
        {campaign.coverImageUrl ? (
          <div
            className="w-full aspect-[16/5.4] mb-8 rounded-lg overflow-hidden bg-gray-100"
            style={{
              backgroundImage: `url(${campaign.coverImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
            role="img"
            aria-label={campaign.title}
          />
        ) : (
          <div className="w-full aspect-[16/5.4] mb-8 rounded-lg overflow-hidden flex items-center justify-center bg-gray-100">
            <div className="text-gray-400 text-center">
              <svg
                className="w-12 h-12 mx-auto mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-sm text-gray-500">No cover image</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-4">
              {(tenant.thumbnailLogoUrl || tenant.logoUrl) && (
                <div className="relative w-16 h-16">
                  <Image
                    src={tenant.thumbnailLogoUrl || tenant.logoUrl!}
                    alt={tenant.name}
                    fill
                    className="object-contain"
                    unoptimized
                  />
                </div>
              )}
              <div>
                <h2 className="text-lg font-medium text-gray-900">{tenant.name}</h2>
                <p className="text-gray-600">by {campaign.orgName}</p>
              </div>
            </div>
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(campaign.status)}`}>
              {getStatusLabel(campaign.status)}
            </span>
          </div>

          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {campaign.title}
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 whitespace-pre-line">
            {campaign.description}
          </p>

          {/* Action Buttons */}
          <div className="flex gap-4 mb-8">
            {canParticipate ? (
              <Link
                href={`/${tenant.slug}/campaigns/${campaign.slug}/locations`}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                style={{
                  backgroundColor: tenant.primaryColor || undefined
                }}
              >
                Start Scavenger Hunt
              </Link>
            ) : (
              <Link
                href={`/${tenant.slug}`}
                className="text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-colors"
                style={{ backgroundColor: 'var(--theme-secondary, #6B7280)' }}
              >
                View Organization
              </Link>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{clues.length}</div>
              <div className="text-sm text-gray-600">Locations</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{campaign.participantCount}</div>
              <div className="text-sm text-gray-600">Participants</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {campaign.participantCap || "∞"}
              </div>
              <div className="text-sm text-gray-600">Participant Limit</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">
                {campaign.mintCap || "∞"}
              </div>
              <div className="text-sm text-gray-600">Asset Limit</div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Campaign Details */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold mb-6" style={{ color: tenant.primaryColor || '#111827' }}>Campaign Details</h2>
              
              <div className="space-y-4">
                {campaign.orgDescription && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">About the Organization</h3>
                    <p className="text-gray-600">{campaign.orgDescription}</p>
                  </div>
                )}

                {campaign.locationText && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Location</h3>
                    <p className="text-gray-600 flex items-center">
                      <span className="mr-2">📍</span>
                      {campaign.locationText}
                    </p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Start Date</h3>
                    <p className="text-gray-600">{formatDateTime(campaign.startAt)}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">End Date</h3>
                    <p className="text-gray-600">{formatDateTime(campaign.endAt)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Campaign Map - Debug Entry Point */}
            {DEBUG_FLAGS.showMapDebugInfo && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800 font-medium">🔍 Map Section Debug:</p>
                <p>campaign.showMap: {String(campaign.showMap)}</p>
                <p>Type of showMap: {typeof campaign.showMap}</p>
                <p>Campaign object keys: {Object.keys(campaign).join(', ')}</p>
              </div>
            )}

            {campaign.showMap && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold mb-6" style={{ color: tenant.primaryColor || '#111827' }}>Hunt Locations</h2>
                
                {/* Debug: Show what we're working with */}
                {DEBUG_FLAGS.showMapDebugInfo && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                    <p><strong>🔍 Map Debug:</strong></p>
                    <p>Show Map: {campaign.showMap ? 'Yes' : 'No'}</p>
                    <p>Total Clues: {clues.length}</p>
                    <p>Clues with coordinates: {clues.filter(clue => clue.latitude && clue.longitude).length}</p>
                    <p>Filtered locations: {JSON.stringify(clues.filter(clue => clue.latitude && clue.longitude).map(c => ({ title: c.title, lat: c.latitude, lng: c.longitude })))}</p>
                  </div>
                )}

                {clues.some(clue => clue.latitude && clue.longitude) ? (
                  <div>
                    {DEBUG_FLAGS.showMapDebugInfo && (
                      <p className="mb-4 text-green-600 font-medium">✅ Rendering CampaignOverviewMap component...</p>
                    )}
                    <CampaignOverviewMap
                      locations={clues
                        .filter(clue => clue.latitude && clue.longitude)
                        .map(clue => ({
                          id: clue.id,
                          title: clue.title,
                          address: clue.hideAddress ? undefined : clue.address,
                          latitude: clue.latitude!,
                          longitude: clue.longitude!,
                          radiusMiles: clue.radiusMiles,
                          hideAddress: clue.hideAddress
                        }))
                      }
                      height="500px"
                    />
                  </div>
                ) : (
                  <div className="p-8 border-2 border-dashed border-gray-300 rounded-lg text-center">
                    <div className="text-gray-500">
                      <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Map Not Available</h3>
                      <p className="text-gray-600">
                        This hunt&apos;s locations don&apos;t have map coordinates configured yet.
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Check the location list below for address details.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Debug Info */}
            {DEBUG_FLAGS.showMapDebugInfo && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
                <h3 className="font-semibold text-yellow-800 mb-2">Debug Info:</h3>
                <p><strong>Show Map:</strong> {campaign.showMap ? 'Yes' : 'No'}</p>
                <p><strong>Clues with coordinates:</strong> {clues.filter(clue => clue.latitude && clue.longitude).length}</p>
                <p><strong>Google Maps API Key:</strong> {process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ? 'Set' : 'Not set'}</p>
                <details className="mt-2">
                  <summary className="cursor-pointer text-yellow-700">Clue Data</summary>
                  <pre className="mt-2 text-xs bg-white p-2 rounded overflow-auto">
                    {JSON.stringify(clues.map(c => ({
                      title: c.title,
                      hasCoords: !!(c.latitude && c.longitude),
                      latitude: c.latitude,
                      longitude: c.longitude,
                      radiusMiles: c.radiusMiles,
                      hideAddress: c.hideAddress
                    })), null, 2)}
                  </pre>
                </details>
              </div>
            )}

            {/* How It Works */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold mb-6" style={{ color: tenant.primaryColor || '#111827' }}>How It Works</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-blue-600">1</span>
                  </div>
                  <h3 className="font-semibold mb-2 text-gray-900">Find Locations</h3>
                  <p className="text-gray-600 text-sm">
                    Visit each location and look for the IYK NFC tag
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-green-600">2</span>
                  </div>
                  <h3 className="font-semibold mb-2 text-gray-900">Scan & Claim</h3>
                  <p className="text-gray-600 text-sm">
                    Tap your phone on the NFC tag to claim your digital reward
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-purple-600">3</span>
                  </div>
                  <h3 className="font-semibold mb-2 text-gray-900">Collect All</h3>
                  <p className="text-gray-600 text-sm">
                    Complete the hunt by collecting all digital assets
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Locations */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold" style={{ color: tenant.primaryColor || '#111827' }}>Locations ({clues.length})</h2>
                <span className={`text-xs px-2 py-1 rounded ${
                  canParticipate 
                    ? "text-green-600 bg-green-50" 
                    : "text-blue-600 bg-blue-50"
                }`}>
                  {canParticipate ? "Click to visit" : "Click to view"}
                </span>
              </div>
              <div className="space-y-3">
                {clues.map((clue, index) => (
                  <Link
                    key={clue.id}
                    href={`/${tenant.slug}/campaigns/${campaign.slug}/locations/${clue.slug}`}
                    className={`block border rounded-lg p-3 hover:shadow-md transition-all cursor-pointer ${
                      canParticipate 
                        ? "hover:bg-gray-50" 
                        : "bg-gray-50 opacity-75 hover:opacity-90"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className={`text-xs font-medium px-2 py-1 rounded mr-2 ${
                          canParticipate 
                            ? "bg-gray-100 text-gray-800" 
                            : "bg-gray-200 text-gray-600"
                        }`}>
                          {index + 1}
                        </span>
                        <h3 className={`font-medium text-sm ${
                          canParticipate ? "text-gray-900" : "text-gray-700"
                        }`}>
                          {clue.title}
                        </h3>
                      </div>
                      <svg className={`w-4 h-4 ${
                        canParticipate ? "text-gray-400" : "text-gray-300"
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                    <p className={`text-xs mb-1 ${
                      canParticipate ? "text-gray-600" : "text-gray-500"
                    }`}>
                      {clue.description}
                    </p>
                    <p className={`text-xs flex items-center ${
                      canParticipate ? "text-gray-500" : "text-gray-400"
                    }`}>
                      <span className="mr-1">📍</span>
                      {clue.hideAddress && clue.radiusMiles 
                        ? `Within ${clue.radiusMiles} mile${clue.radiusMiles !== 1 ? 's' : ''} of this area`
                        : clue.address || 'Location not specified'
                      }
                    </p>
                  </Link>
                ))}
              </div>
              
              {canParticipate && (
                <div className="mt-4">
                  <Link
                    href={`/${tenant.slug}/campaigns/${campaign.slug}/locations`}
                    className="w-full bg-blue-600 text-white text-center py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors block"
                    style={{
                      backgroundColor: tenant.primaryColor || undefined
                    }}
                  >
                    View All Locations
                  </Link>
                </div>
              )}
            </div>

            {/* Organization Info */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-4" style={{ color: tenant.primaryColor || '#111827' }}>Organization</h2>
              <div className="flex items-center space-x-3 mb-4">
                {(tenant.thumbnailLogoUrl || tenant.logoUrl) && (
                  <div className="relative w-12 h-12">
                    <Image
                      src={tenant.thumbnailLogoUrl || tenant.logoUrl!}
                      alt={tenant.name}
                      fill
                      className="object-contain"
                      unoptimized
                    />
                  </div>
                )}
                <div>
                  <h3 className="font-medium text-gray-900">{tenant.name}</h3>
                  <p className="text-sm text-gray-600">{campaign.orgName}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <Link
                  href={`/${tenant.slug}`}
                  className="block w-full bg-gray-100 text-gray-800 text-center py-2 px-4 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  View All Hunts
                </Link>
                
                {campaign.orgWebsite && (
                  <a
                    href={campaign.orgWebsite}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full border border-gray-300 text-gray-700 text-center py-2 px-4 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Visit Website
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
